/** @jsx jsx */
/** @jsxFrag */
import React, { useCallback, useMemo } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { layers } from '@jira/platform__ui-layers';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import type { NewGlyphColorProps } from '@atlaskit/icon/types';
import { Box } from '@atlaskit/primitives/compiled';
import Select, {
	type SingleValueProps,
	type StylesConfig,
	type DropdownIndicatorProps,
	type IndicatorsContainerProps,
	components,
	type ValueType,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AllowedOperators,
	OnChangeOperator,
	OperatorOption,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

const baseStyle = {
	alignSelf: 'center',
	margin: 0,
	padding: 0,
};

const indicatorsContainerStyle = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingBottom: token('space.0'),
			paddingLeft: token('space.0'),
		},
	},
});
const getAdditionalStyles = (isInvalid: boolean): StylesConfig<OperatorOption> => ({
	control: (css, props) => ({
		...css,
		height: `${token('space.300')}`,
		textAlign: 'left',
		// need to overwrite existing minHeight
		minHeight: 0,
		// substracting 5 so it's never on top of main dropdown
		zIndex: layers.navigation - 5,
		backgroundColor: token('color.background.input'),
		...(props.isDisabled
			? {
					backgroundColor: 'none',
					borderColor: token('color.text.inverse'),
				}
			: {}),
	}),
	input: (css) => ({
		...css,
		...baseStyle,
		height: 'inherit',
	}),
	singleValue: (css) => ({
		...css,
		...baseStyle,
		color: token('color.text.selected'),
		textAlign: 'left',
		...(isInvalid
			? {
					color: token('color.text.danger'),
				}
			: {}),
	}),
	valueContainer: (css) => ({
		...css,
		...baseStyle,
		// to align horizontally
		height: '100%',
		paddingLeft: `${token('space.050')}`,
		paddingRight: 0,
	}),
	indicatorsContainer: (css) => ({
		...css,
		...baseStyle,
		'> div': {
			padding: '0',
		},
		// to align horizontally
		height: '100%',
	}),
	container: (css) => ({
		...css,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		textAlign: 'left',
	}),
	menu: (css) => ({
		...css,
		// no space between dropdown and trigger button
		marginTop: 0,
		width: 'fit-content',
		// substracting 4 so it's never on top of main dropdown but if on top of the picker trigger of other operator dropdowns
		zIndex: layers.navigation - 4,
	}),
});

type Props = {
	allowedOperators: AllowedOperators;
	onChangeOperator?: OnChangeOperator;
	selectedOperator?: OperatorOption;
	isParentPickerOpen: boolean;
	onOpenOperatorPicker: () => void;
	isInvalid: boolean;
};

const TEST_ID_DROPDOWN =
	'jql-builder-basic.common.ui.picker-trigger.dropdown-operator.dropdown-operator';

const getOptionLabel = (option: OperatorOption): string => option.ariaLabel;
const formatOptionLabel = (option: OperatorOption): string => option.shortLabel;

const DropdownOperator = ({
	allowedOperators,
	selectedOperator = allowedOperators[0],
	onChangeOperator,
	isParentPickerOpen,
	onOpenOperatorPicker,
	isInvalid,
}: Props): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const onChangeOperatorWithAnalytics = useCallback(
		(option: ValueType<OperatorOption>) => {
			onChangeOperator && onChangeOperator(option);
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			});
			option &&
				fireUIAnalytics(analyticsEvent, 'operatorSelect', {
					option: option.value,
					selectType: 'inline',
				});
		},
		[createAnalyticsEvent, onChangeOperator],
	);

	const SingleValue = useCallback(
		({ children, ...props }: SingleValueProps<OperatorOption>) => (
			<components.SingleValue {...props}>{selectedOperator?.valueLabel}</components.SingleValue>
		),
		[selectedOperator?.valueLabel],
	);

	const DropdownIndicator = useCallback(
		(props: DropdownIndicatorProps<OperatorOption>) => {
			let primaryColor: NewGlyphColorProps['color'] = token('color.text.selected');
			if (props.isDisabled) {
				primaryColor = token('color.text.inverse');
			} else if (isInvalid) {
				primaryColor = token('color.text.danger');
			}

			return (
				<components.DropdownIndicator {...props}>
					<Box paddingInline="space.075">
						<ChevronDownIcon
							testId="jql-builder-basic.common.ui.picker-trigger.dropdown-operator.chevron-down-icon"
							label=""
							color={primaryColor}
							LEGACY_primaryColor={primaryColor}
							size="small"
						/>
					</Box>
				</components.DropdownIndicator>
			);
		},
		[isInvalid],
	);

	const onMenuOpenAnalytics = useCallback(() => {
		onOpenOperatorPicker();

		const analyticsEvent = createAnalyticsEvent({
			action: 'opened',
			actionSubject: 'dropdown',
		});
		fireUIAnalytics(analyticsEvent, 'operatorSelect', {
			selectType: 'inline',
		});
	}, [createAnalyticsEvent, onOpenOperatorPicker]);

	const dropdownOperatorComponents = useMemo(
		() => ({
			DropdownIndicator,
			SingleValue,
			IndicatorsContainer: (props: IndicatorsContainerProps<OperatorOption>) => (
				<components.IndicatorsContainer {...props} xcss={indicatorsContainerStyle.root} />
			),
		}),
		[DropdownIndicator, SingleValue],
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			paddingInlineStart="space.025"
			paddingInlineEnd="space.050"
			onClick={(event: React.MouseEvent<HTMLDivElement>) => {
				!isParentPickerOpen && event.stopPropagation();
			}}
			role="presentation"
			onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
				// Prevent issue list from capturing arrow up and down keys, prevent closing a modal on escape key
				if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Escape') {
					event.stopPropagation();
				}
			}}
		>
			<Select
				aria-label={formatMessage(messages.operatorLabel)}
				testId={TEST_ID_DROPDOWN}
				components={dropdownOperatorComponents}
				spacing="compact"
				appearance="subtle"
				styles={getAdditionalStyles(isInvalid)}
				value={selectedOperator}
				options={allowedOperators}
				onChange={onChangeOperatorWithAnalytics}
				getOptionLabel={getOptionLabel}
				formatOptionLabel={formatOptionLabel}
				isDisabled={isParentPickerOpen}
				isInvalid={isInvalid}
				isSearchable={false}
				onMenuOpen={onMenuOpenAnalytics}
			/>
		</Box>
	);
};

export default DropdownOperator;
